/**
 * PersonalInformationSection Component
 * Handles personal information fields for both Individual and Company user categories
 */

import React from "react";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import UserIcon from "../../../../../images/icons/single-user-icon.svg";
import EmailIcon from "../../../../../images/icons/email.svg";
import PadLock from "../../../../../images/icons/padlock.png";
import EyeIcon from "../../../../../images/icons/eye-icon.svg";
import EyeCrossed from "../../../../../images/icons/eye-crossed.svg";
import birthIcon from "../../../../../images/icons/dat-of-birth.svg";
import PassportlIcon from "../../../../../images/icons/passport.svg";
import TypeContact from "../../../../../images/icons/type-of-contact.svg";
import CompanyDocumentField from "../../../elements/CompanyDocumentField.jsx";
import MonthYearDatePicker from '../../../../ui/MonthYearDatePicker';

// Skeleton loader styles
const skeletonStyle = {
  backgroundColor: "#e5e7eb",
  borderRadius: "4px",
  animation: "pulse 1.5s ease-in-out infinite",
};

// Input Field Skeleton (for text inputs)
const InputFieldSkeleton = () => (
  <div className="inner-detail">
    <span className="icon"></span>
    <div className="inner-detail-inner">
      <div
        style={{
          width: "100%",
          height: "40px",
          ...skeletonStyle,
        }}
      />
    </div>
  </div>
);

// Date Picker Skeleton (for date picker fields)
const DatePickerSkeleton = () => (
  <div className="inner-detail">
    <div className="date-picker-outer date-picker-outer-all">
      <div
        style={{
          width: "100%",
          height: "40px",
          ...skeletonStyle,
        }}
      />
    </div>
    <span className="icon"></span>
  </div>
);

const PersonalInformationSection = ({
  isLoading,
  fieldErrors = {},
  clearFieldError,
  userCategory,
  userCategoryName,
  // Individual fields
  firstName,
  setFirstName,
  middleName,
  setMiddleName,
  lastName,
  setLastName,
  // Company fields
  companyName,
  setCompanyName,
  companyFirstName,
  setCompanyFirstName,
  companyLastName,
  setCompanyLastName,
  registrationNumber,
  setRegistrationNumber,
  vatId,
  setVatId,
  companyDocument,
  userView,
  downloadCompyDoc,
  handleRegistrationDocChange,
  // Common fields
  userView: userViewData,
  email,
  password,
  setPassword,
  passwordConfirmation,
  setPasswordConfirmation,
  showPassword,
  setShowPassword,
  passwordError,
  passwordMatchError,
  passwordRef,
  passwordConfirmationRef,
  selectedDate,
  handleDateChange,
  joiningDate,
  handleJoiningDateChange,
  passport,
  setPassport,
  appSettings,
  user,
  SUPER_ADMIN,
  ADMIN,
}) => {
  return (
    <div className="information-inner">
      <div className="information">
        <div className="information-title">
          Personal Information
        </div>
        <div className="information-outer">
          <div className="information-left">
            {(userCategory === userCategoryName.Business ||
              userCategory === userCategoryName.Vendor ||
              userCategory === userCategoryName.Client) ? (
              <>
                <div className="inner-detail-outer" id="field-companyName">
                  <div className="inner-detail">
                    <span className="icon"><img src={UserIcon} alt="user icon" /></span>
                    <div className="inner-detail-inner">
                      <label htmlFor="compname">Company Name *</label>
                    </div>
                  </div>
                  {isLoading ? (
                    <InputFieldSkeleton />
                  ) : (
                    <div className="inner-detail">
                      <span className="icon"></span>
                      <div className="inner-detail-inner">
                        <input type="text" id="compname" value={companyName ?? ''} onChange={(e) => { setCompanyName(e.target.value); clearFieldError?.('companyName'); }} required className={fieldErrors.companyName ? 'field-error' : ''} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="inner-detail-outer">
                  <div className="inner-detail">
                    <span className="icon"><img src={UserIcon} alt="user icon" /></span>
                    <div className="inner-detail-inner" id="field-companyFirstName">
                      <label htmlFor="compfirstname">First Name *</label>
                    </div>
                  </div>
                  {isLoading ? (
                    <InputFieldSkeleton />
                  ) : (
                    <div className="inner-detail">
                      <span className="icon"></span>
                      <div className="inner-detail-inner">
                        <input type="text" id="compfirstname" value={companyFirstName ?? ''} onChange={(e) => { setCompanyFirstName(e.target.value); clearFieldError?.('companyFirstName'); }} required className={fieldErrors.companyFirstName ? 'field-error' : ''} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="inner-detail-outer">
                  <div className="inner-detail">
                    <span className="icon"><img src={UserIcon} alt="user icon" /></span>
                    <div className="inner-detail-inner">
                      <label htmlFor="complastname">Last Name</label>
                    </div>
                  </div>
                  {isLoading ? (
                    <InputFieldSkeleton />
                  ) : (
                    <div className="inner-detail">
                      <span className="icon"></span>
                      <div className="inner-detail-inner">
                        <input type="text" id="complastname" value={companyLastName ?? ''} onChange={(e) => setCompanyLastName(e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="inner-detail-outer">
                  <div className="inner-detail">
                    <span className="icon"><img src={UserIcon} alt="user icon" /></span>
                    <div className="inner-detail-inner" id="field-registrationNumber">
                      <label htmlFor="regnum">Registration Number *</label>
                    </div>
                  </div>
                  {isLoading ? (
                    <InputFieldSkeleton />
                  ) : (
                    <div className="inner-detail">
                      <span className="icon"></span>
                      <div className="inner-detail-inner">
                        <input type="text" id="regnum" value={registrationNumber ?? ''} onChange={(e) => { setRegistrationNumber(e.target.value); clearFieldError?.('registrationNumber'); }} required className={fieldErrors.registrationNumber ? 'field-error' : ''} />
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="inner-detail-outer">
                  <div className="inner-detail">
                    <span className="icon"><img src={UserIcon} alt="user icon" /></span>
                    <div className="inner-detail-inner" id="field-firstName">
                      <label htmlFor="fname">First Name *</label>
                    </div>
                  </div>
                  {isLoading ? (
                    <InputFieldSkeleton />
                  ) : (
                    <div className="inner-detail">
                      <span className="icon"></span>
                      <div className="inner-detail-inner">
                        <input type="text" id="fname" value={firstName ?? ''} onChange={(e) => { setFirstName(e.target.value); clearFieldError?.('firstName'); }} required className={fieldErrors.firstName ? 'field-error' : ''} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="inner-detail-outer">
                  <div className="inner-detail">
                    <span className="icon"><img src={UserIcon} alt="user icon" /></span>
                    <div className="inner-detail-inner">
                      <label htmlFor="mname">Middle Name</label>
                    </div>
                  </div>
                  {isLoading ? (
                    <InputFieldSkeleton />
                  ) : (
                    <div className="inner-detail">
                      <span className="icon"></span>
                      <div className="inner-detail-inner">
                        <input type="text" id="mname" value={middleName ?? ''} onChange={(e) => setMiddleName(e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="inner-detail-outer">
                  <div className="inner-detail">
                    <span className="icon"><img src={UserIcon} alt="user icon" /></span>
                    <div className="inner-detail-inner" id="field-lastName">
                      <label htmlFor="lname">Last Name *</label>
                    </div>
                  </div>
                  {isLoading ? (
                    <InputFieldSkeleton />
                  ) : (
                    <div className="inner-detail">
                      <span className="icon"></span>
                      <div className="inner-detail-inner">
                        <input type="text" id="lname" value={lastName ?? ''} onChange={(e) => { setLastName(e.target.value); clearFieldError?.('lastName'); }} required className={fieldErrors.lastName ? 'field-error' : ''} />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            {(user && (user.userType === SUPER_ADMIN || user.userType === ADMIN)) ? (
              <div className="inner-detail-outer">
                <div className="inner-detail">
                  <span className="icon"><img src={birthIcon} alt="birth icon" /></span>
                  <div className="inner-detail-inner">
                    <label>Employment Start Date</label>
                  </div>
                </div>
                {isLoading ? (
                  <DatePickerSkeleton />
                ) : (
                  <div className="inner-detail">
                    <div className="date-picker-outer date-picker-outer-all">
                      <div className="custoom-icon-calender custoom-icon-calender-all">
                        <MonthYearDatePicker
                          value={joiningDate}
                          onChange={handleJoiningDateChange}
                          placement="bottomEnd"
                          minYear={new Date().getFullYear() - 50}
                          maxYear={new Date().getFullYear() + 1}
                          maxDate={new Date()}
                          isoWeek={appSettings.weekStartDay && appSettings.weekStartDay === 'Monday'}
                        />
                      </div>
                    </div>
                    <span className="icon"></span>
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <div className="information-right">
            <div className="inner-detail-outer">
              <div className="inner-detail">
                <span className="icon"><img src={EmailIcon} alt="email icon" /></span>
                <div className="inner-detail-inner">
                  <label htmlFor="email">Email</label>
                </div>
              </div>
              {isLoading ? (
                <InputFieldSkeleton />
              ) : (
                <div className="inner-detail">
                  <span className="icon"></span>
                  <div className="inner-detail-inner">
                    <input type="email" className="read-only-input" id="email" value={userViewData?.email ?? ''} required readOnly />
                  </div>
                </div>
              )}
            </div>
            <div className="inner-detail-outer">
              <div className="inner-detail">
                <span className="icon"><img width={'20px'} src={PadLock} alt="email icon" /></span>
                <div className="inner-detail-inner">
                  <label htmlFor="password">Password</label>
                </div>
              </div>
              {isLoading ? (
                <InputFieldSkeleton />
              ) : (
                <div className="inner-detail">
                  <span className="icon"></span>
                  <div className="inner-detail-inner pass-eye">
                    <input type={showPassword ? "text" : "password"} id="password" value={password ?? ''} onChange={(e) => setPassword(e.target.value)} ref={passwordRef} autoComplete="new-password" />
                    <img
                      src={showPassword ? EyeCrossed : EyeIcon}
                      className="password-toggle-icon"
                      alt="Toggle Password"
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </div>
                  {passwordError && (
                    <span className="text-red-500 text-xs italic">
                      {passwordError}
                    </span>
                  )}
                </div>
              )}
            </div>
            {password && (
              <div className="inner-detail-outer">
                <div className="inner-detail">
                  <span className="icon"><img width={'20px'} src={PadLock} alt="email icon" /></span>
                  <div className="inner-detail-inner">
                    <label htmlFor="confpassword">Confirm Password</label>
                  </div>
                </div>
                {isLoading ? (
                  <InputFieldSkeleton />
                ) : (
                  <div className="inner-detail">
                    <span className="icon"></span>
                    <div className="inner-detail-inner">
                      <input type="password" id="confpassword" value={passwordConfirmation ?? ''} onChange={(e) => setPasswordConfirmation(e.target.value)} ref={passwordConfirmationRef} />
                    </div>
                    {passwordMatchError && (
                      <span className="text-red-500 text-xs italic">
                        {passwordMatchError}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            {(userCategory === userCategoryName.Business ||
              userCategory === userCategoryName.Vendor ||
              userCategory === userCategoryName.Client) && (
                <>
                  <div className="inner-detail-outer">
                    <div className="inner-detail">
                      <span className="icon"><img src={UserIcon} alt="email icon" /></span>
                      <div className="inner-detail-inner">
                        <label htmlFor="vatid">Vat Id</label>
                      </div>
                    </div>
                    {isLoading ? (
                      <InputFieldSkeleton />
                    ) : (
                      <div className="inner-detail">
                        <span className="icon"></span>
                        <div className="inner-detail-inner">
                          <input type="text" id="vatid" value={vatId ?? ''} onChange={(e) => setVatId(e.target.value)} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="inner-detail-outer">
                    <div className="inner-detail">
                      <span className="icon"><img src={TypeContact} alt="contact" /></span>
                      <div className="inner-detail-inner">
                        <label htmlFor="compregdoc">Company Registration Document</label>
                      </div>
                    </div>
                    {isLoading ? (
                      <InputFieldSkeleton />
                    ) : (
                      <div className="inner-detail">
                        <span className="icon"></span>
                        <div className="inner-detail-inner">
                          {companyDocument ? (
                            <div>
                              <p>Current Document:
                                <div style={{ cursor: "pointer" }} onClick={() => downloadCompyDoc(userViewData.company_document)}>
                                  <CompanyDocumentField companyDocument={companyDocument} />
                                </div>
                              </p>
                            </div>
                          ) : (
                            <p>No document uploaded yet.</p>
                          )}
                          <input type="file" id="compregdoc" onChange={handleRegistrationDocChange} />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            <>
              <div className="inner-detail-outer">
                <div className="inner-detail">
                  <span className="icon"><img src={birthIcon} alt="birth icon" /></span>
                  <div className="inner-detail-inner">
                    <label>Date of birth</label>
                  </div>
                </div>
                {isLoading ? (
                  <DatePickerSkeleton />
                ) : (
                  <div className="inner-detail">
                    <div className="date-picker-outer date-picker-outer-all">
                      <div className="custoom-icon-calender custoom-icon-calender-all">
                        <MonthYearDatePicker
                          value={selectedDate}
                          onChange={handleDateChange}
                          placement="bottomEnd"
                          minYear={1920}
                          maxYear={new Date().getFullYear()}
                          maxDate={new Date()}
                          isoWeek={appSettings.weekStartDay && appSettings.weekStartDay === 'Monday'}
                        />
                      </div>
                    </div>
                    <span className="icon"></span>
                  </div>
                )}
              </div>
              <div className="inner-detail-outer" id="field-passport">
                <div className="inner-detail">
                  <span className="icon"><img src={PassportlIcon} alt="passport icon" /></span>
                  <div className="inner-detail-inner">
                    <label htmlFor="id_number">Passport or ID Number *</label>
                  </div>
                </div>
                {isLoading ? (
                  <InputFieldSkeleton />
                ) : (
                  <div className="inner-detail">
                    <span className="icon"></span>
                    <div className="inner-detail-inner">
                      <input type="text" id="id_number" value={passport ?? ''} onChange={(e) => { setPassport(e.target.value); clearFieldError?.('passport'); }} required className={fieldErrors.passport ? 'field-error' : ''} />
                    </div>
                  </div>
                )}
              </div>
            </>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default PersonalInformationSection;
